import React, { useState, useEffect } from "react";
import { Users, AlertTriangle, Search } from "lucide-react";
import { Modal } from "../Modal";
import api, { apiErr } from "../../lib/api";
import { toast } from "sonner";

const RESULT_LIMIT = 50;

/**
 * SPT-PTE-2026-0040 — correct a mis-attributed customer on a job (any
 * status, including closed/paid — a wrong customer is a data-integrity
 * problem, not a workflow one). Manager/admin only. Re-points the job,
 * every one of its job_items AND their linked equipment records onto the
 * newly picked customer via PATCH /jobs/{id}/customer.
 *
 * Results panel is a tall, always-visible inline list (not a small floating
 * dropdown) so the full picker is easy to browse/search — loads the top 50
 * customers immediately (even before typing) and re-queries on every
 * keystroke, with a "refine your search" hint if the result is truncated.
 */
export function ChangeJobCustomerModal({ jobId, currentName, deliveryNoteNumber, onClose, onDone }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/customers", { params: { search: query || undefined, limit: RESULT_LIMIT } });
        setResults(r.data.items || []);
        setTotal(r.data.total || 0);
      } catch { setResults([]); setTotal(0); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const save = async () => {
    if (!picked) { toast.error("Search and pick the correct customer first."); return; }
    setBusy(true);
    try {
      await api.patch(`/jobs/${jobId}/customer`, { customer_id: picked.id });
      toast.success(`Customer corrected to ${picked.customer_name}`);
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Users} title="Change customer"
      subtitle={`DN ${deliveryNoteNumber || ""} · currently ${currentName || "—"}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="change-customer-cancel">Cancel</button>
        <button className="btn gold" onClick={save} disabled={busy || !picked} data-testid="change-customer-save">
          {busy ? "Saving…" : "Save"}
        </button>
      </>}>
      <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column" }}>
        <div className="card pad" style={{ background: "#FBF3D6", marginBottom: 12, fontSize: 12.5, display: "flex", gap: 8, flexShrink: 0 }}>
          <AlertTriangle size={15} style={{ color: "var(--gold-deep)", flexShrink: 0 }} />
          <span>This corrects a data-entry mistake — it moves this job, every line on it, AND their linked
            equipment records onto the new customer. Works at any job status, including Closed.</span>
        </div>

        {picked ? (
          <>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
              textTransform: "uppercase", color: "var(--text-3)", marginBottom: 4 }}>New customer</label>
            <div className="input" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
              data-testid="change-customer-selected">
              <span>{picked.customer_number && <span className="mono" style={{ color: "var(--gold-deep)", marginRight: 8 }}>{picked.customer_number}</span>}{picked.customer_name}</span>
              <button className="icon-btn" onClick={() => setPicked(null)} data-testid="change-customer-clear">×</button>
            </div>
          </>
        ) : (
          <>
            <div className="input" style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, marginBottom: 8 }}>
              <Search size={13} color="var(--text-3)" />
              <input className="mono" style={{ border: "none", outline: "none", width: "100%", background: "none" }}
                placeholder="Search customer by name / number…" value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus data-testid="change-customer-search" />
            </div>
            <div style={{ flex: 1, minHeight: 420, maxHeight: "48vh", overflowY: "auto",
              border: "1px solid var(--line)", borderRadius: 10, padding: 4 }}
              data-testid="change-customer-results">
              {loading && <div className="t-mut" style={{ fontSize: 12, padding: "10px 12px" }}>Searching…</div>}
              {!loading && results.length === 0 && (
                <div className="t-mut" style={{ fontSize: 12, padding: "10px 12px" }}>
                  No customers found{query ? ` for "${query}"` : ""}.
                </div>
              )}
              {!loading && results.map((r) => (
                <button key={r.id} type="button" className="nav-item"
                  style={{ color: "var(--text)", borderRadius: 6, width: "100%", textAlign: "left", padding: "9px 10px", display: "block" }}
                  onClick={() => setPicked(r)} data-testid={`change-customer-opt-${r.id}`}>
                  {r.customer_number && <span className="mono" style={{ color: "var(--gold-deep)", marginRight: 8 }}>{r.customer_number}</span>}
                  {r.customer_name}
                </button>
              ))}
            </div>
            {!loading && total > results.length && (
              <div className="t-mut" style={{ fontSize: 11, marginTop: 6, flexShrink: 0 }} data-testid="change-customer-truncated-hint">
                Showing top {results.length} of {total} matches — keep typing to narrow it down.
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
